import { useParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { ConciergeButton } from "@/components/Concierge";
import { Reveal, Eyebrow, CTABand, PortraitArt, CONCERN_CHIP, Chips, FocusChips } from "@/components/Shared";
import { getTeamMember } from "@/data/content";
import { getConcern } from "@/data/concerns";
import NotFound from "@/pages/NotFound";

const TeamProfile = () => {
  const { slug } = useParams();
  const member = getTeamMember(slug);
  if (!member) return <NotFound />;

  const concernChips = (member.concerns || [])
    .map((path) => {
      const [family, cslug] = path.split("/");
      const concern = getConcern(family, cslug);
      return concern ? CONCERN_CHIP(path, concern.title) : null;
    })
    .filter(Boolean);

  return (
    <>
      <SEO
        title={`${member.name} | Lumière Aesthetics`}
        description={`${member.name}, ${member.title} at Lumière Aesthetics, Cottesloe. ${member.descriptor}.${member.ahpra ? ` AHPRA ${member.ahpra}.` : ""}`}
        ogImage={member.photo}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": member.ahpra?.startsWith("MED") ? "Physician" : "Person",
              name: member.name,
              jobTitle: member.title,
              ...(member.ahpra ? { identifier: member.ahpra } : {}),
              url: `https://www.lumiereaesthetics.com.au/team/${member.slug}`,
              ...(member.photo ? { image: `https://www.lumiereaesthetics.com.au${member.photo}` } : {}),
              worksFor: { "@type": "MedicalClinic", name: "Lumière Aesthetics", address: "Shop 3/40 Marine Parade, Cottesloe WA 6011", telephone: "+61 8 6388 4474" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://www.lumiereaesthetics.com.au/" },
                { "@type": "ListItem", position: 2, name: "The Lumière Team", item: "https://www.lumiereaesthetics.com.au/team" },
                { "@type": "ListItem", position: 3, name: member.name, item: `https://www.lumiereaesthetics.com.au/team/${member.slug}` },
              ],
            },
          ],
        }}
      />

      <section className="bg-warm-white pt-32 lg:pt-40 pb-24" data-testid="team-profile">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <PortraitArt photo={member.photo} name={member.name} className="aspect-[4/5] rounded-lg" />
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-8">
            <Reveal>
              <Link to="/team" data-testid="back-to-team" className="eyebrow hover:text-sea-green transition-colors">← The Lumière Team</Link>
              <div className="mt-6">
                <Eyebrow>{member.title}</Eyebrow>
                <h1 className="text-deep-sea">{member.name}</h1>
                <p className="font-serif italic text-sea-green text-xl mt-3">{member.descriptor}</p>
                {member.ahpra && <p className="text-soft-grey text-sm mt-3" data-testid="ahpra-number">AHPRA registration {member.ahpra}</p>}
              </div>

              <div className="text-ink/85 space-y-5 leading-[1.8] mt-10 text-[17px]">
                {member.bio.map((p, i) => <p key={i}>{p}</p>)}
              </div>

              {member.seesFor?.length > 0 && (
                <div className="mt-12">
                  <h3 className="text-deep-sea">At Lumière I see patients for</h3>
                  <ul className="mt-5 space-y-2.5">
                    {member.seesFor.map((item, i) => (
                      <li key={i} className="flex gap-3 text-ink/85 text-[15px]">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2.5 shrink-0" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {member.focus?.length > 0 && (
                <div className="mt-12">
                  <h3 className="text-deep-sea mb-5">Areas of focus</h3>
                  <FocusChips items={member.focus} size="md" />
                </div>
              )}

              {concernChips.length > 0 && (
                <div className="mt-12">
                  <h3 className="text-deep-sea mb-5">Concerns I help with</h3>
                  <Chips items={concernChips} />
                </div>
              )}

              {member.note && (
                <div className="mt-12 border-l-2 border-gold pl-8 py-2" data-testid="practice-note">
                  <p className="eyebrow mb-3">A note on how I practise</p>
                  <p className="font-serif italic text-sea-green text-xl leading-relaxed">{member.note}</p>
                </div>
              )}

              <div className="mt-12 flex flex-col sm:flex-row gap-4">
                {member.group === "practice" ? (
                  <Link to="/contact" data-testid="profile-contact-link" className="btn-gold inline-block px-7 py-3.5 text-sm font-medium tracking-[0.06em]">
                    Contact the practice
                  </Link>
                ) : (
                  <ConciergeButton
                    label={`Request a consultation with ${member.name}`}
                    practitioner={member.name}
                    testId="profile-book-button"
                    className="btn-gold inline-block px-7 py-3.5 text-sm font-medium tracking-[0.06em] text-center"
                  />
                )}
                {member.ctaAlt && (
                  <Link
                    to={member.ctaAlt.to}
                    data-testid="profile-alt-cta"
                    className="btn-ghost-sea inline-block px-7 py-3.5 text-sm font-medium tracking-[0.06em] text-center"
                  >
                    {member.ctaAlt.label}
                  </Link>
                )}
              </div>
              {member.ctaAlt?.note && (
                <p className="text-soft-grey text-xs mt-3" data-testid="profile-cta-note">{member.ctaAlt.note}</p>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
};

export default TeamProfile;
